/* eslint-disable no-param-reassign */
/* eslint-disable react/no-array-index-key */
import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { classNames } from "react-extras";

const useStyles = makeStyles(() => ({
  root: {},
  /* Styles applied to the ol element. */
  ol: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    padding: 0,
    margin: 0,
    listStyle: "none",
  },
  /* Styles applied to the li element. */
  li: {
    overflow: "hidden",
  },
  /* Styles applied to the separator element. */
  separator: {
    display: "flex",
    userSelect: "none",
    marginLeft: 8,
    marginRight: 8,
  },
}));

function insertSeparators(
  items: JSX.Element[],
  className: string,
  separator: string,
) {
  return items.reduce((acc: JSX.Element[], current, index) => {
    if (index < items.length - 1) {
      acc = acc.concat(
        current,
        <li aria-hidden key={`separator-${index}`} className={className}>
          {separator}
        </li>,
      );
    } else {
      acc.push(current);
    }

    return acc;
  }, []);
}

interface Props {
  children: React.ReactNode;
  className?: string;
  itemsAfterCollapse?: number;
  itemsBeforeCollapse?: number;
  maxItems?: number;
  separator?: string;
}

const CustomBreadcrumbs = React.forwardRef<HTMLElement, Props>(
  function CustomBreadcrumbs(props: Props, ref) {
    const {
      children,
      className,
      itemsAfterCollapse = 1,
      itemsBeforeCollapse = 1,
      maxItems = 8,
      separator = "/",
      ...other
    } = props;

    const classes = useStyles();

    const renderItemsBeforeAndAfter = (allItems: JSX.Element[]) => {
      if (itemsBeforeCollapse + itemsAfterCollapse >= allItems.length) {
        return allItems;
      }

      return [
        ...allItems.slice(0, itemsBeforeCollapse),
        <li className={classes.li} key="sep">
          ...
        </li>,
        ...allItems.slice(
          allItems.length - itemsAfterCollapse,
          allItems.length,
        ),
      ];
    };

    const allItems = React.Children.toArray(children)
      .filter((child) => {
        return React.isValidElement(child);
      })
      .map((child, index) => (
        <li className={classes.li} key={`child-${index}`}>
          {child}
        </li>
      ));

    return (
      <Typography
        ref={ref}
        component="nav"
        color="textSecondary"
        className={classNames(classes.root, className)}
        {...other}
      >
        <ol className={classes.ol}>
          {insertSeparators(
            maxItems && allItems.length <= maxItems
              ? allItems
              : renderItemsBeforeAndAfter(allItems),
            classes.separator,
            separator,
          )}
        </ol>
      </Typography>
    );
  },
);

export default CustomBreadcrumbs;
